import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import { useGetDetailParentPath } from 'src/routes/useGetDetailParentPath';

import { useDeleteImpact } from '@/hooks/mutations/impact/useDeleteImpact';
import { useGetImpactById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';
import { impactDetailsUrl } from '@/utils/urls';

type Props = {
  activeTabId: 'details' | 'ratings';
};

const Page: FC<Props> = ({ activeTabId }) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const [showTabSettings, setShowTabSettings] = useState(false);
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const impactId = useGetGuidParam('impactId');
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'impacts' });
  const defaultTitle = st('fallback_title');
  const { deleteImpact, loading: deleteLoading } = useDeleteImpact();
  const { data, error } = useGetImpactById({
    queryArgs: {
      id: impactId,
    },
  });
  if (error) {
    throw error;
  }
  const impact = data?.impact?.[0];
  const tabs = useTabs({
    parentType: Parent_Type_Enum.Impact,
    parent: impact,
    hrefRoot: impactDetailsUrl(impactId),
  });
  const parentPath = useGetDetailParentPath(impactId);
  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      await deleteImpact({ Id: impactId });
      setIsDeleteModalVisible(false);
      navigate(parentPath);

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  if (data?.impact.length === 0) {
    throw new PageNotFound(`Impact with id ${impactId} not found`);
  }
  const counter =
    impact &&
    `(${getFriendlyId(Parent_Type_Enum.Impact, impact.SequentialId)})`;

  // Object-level toolbar (CUS-155) — flag-gated. Relocates Delete into the
  // toolbar's overflow ⋮ menu (danger zone) and surfaces the Save disc / Cancel
  // X (save state bridged from the active tab's form via
  // useObjectToolbarFormStore). The Impacts detail page has no export action, so
  // the toolbar omits the Download group (parity with the legacy header).
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title: impact?.Name ?? defaultTitle,
        // Pass the already-parenthesised counter (the toolbar renders it verbatim).
        counter: counter || undefined,
      },
      parentObject: impact,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('objectToolbar.formSettings'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('objectToolbar.tabSettings'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        danger: [
          {
            id: 'delete',
            label: st('delete_button'),
            iconName: 'trash-01',
            permission: 'delete:impact',
            onClick: () => setIsDeleteModalVisible(true),
          },
        ],
      },
    }),
    [impact, defaultTitle, counter, t, st, formConfig]
  );

  return (
    <PageLayout
      title={impact?.Name ?? defaultTitle}
      meta={{
        title: defaultTitle,
      }}
      counter={counter}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Permission permission={'delete:impact'} parentObject={impact}>
            <Button
              variant={'normal'}
              formAction={'none'}
              onClick={() => {
                setIsDeleteModalVisible(true);
              }}
            >
              {st('delete_button')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <ControlledTabs
        activeTabId={activeTabId}
        tabs={tabs}
        variant={'container'}
        parentType={Parent_Type_Enum.Impact}
        parent={impact}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.Impact}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={impact}
      />
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
